import { createSelector } from 'reselect';
import { getResourceEntity } from 'js/lib/PipelineUtils';

export const getEntities = (s) => s.entities || {};
export const getFlowNodes = (s) => s.entities?.flowNodes || {};
export const getDataSets = (s) => s.entities?.dataSets || {};
export const getBranches = (s) => s.pipelines?.branches || {};

/**
 * Memoized flowId → nexsetId lookup, built once per entities change instead
 * of the repeated `Object.values(dataSets/flowNodes).find(...)` scans BYO
 * canvas nodes (ByoDestShellNode) ran on every render — cost was O(nodes ×
 * entities) since react-redux can't cache an inline selector. Mirrors the
 * exact fallback order + tie-break of the original inline `.find()` calls: a
 * dataset's own origin_node_id wins first, then a DATA_SET flow node
 * matching via origin_node_id OR parent_node_id — whichever appears first in
 * entity iteration order, same as the original single-pass `.find()` (the
 * `idx` tracked below is what lets two independent per-condition maps
 * reproduce that single-pass tie-break).
 */
export const getNexsetLookup = createSelector(
  [getDataSets, getFlowNodes],
  (dataSets, flowNodes) => {
    const byDatasetOrigin = {};
    Object.values(dataSets).forEach((d) => {
      const originId = d?.origin_node_id;
      if (originId != null && byDatasetOrigin[String(originId)] === undefined) {
        byDatasetOrigin[String(originId)] = d.id;
      }
    });

    const byNodeOrigin = {};
    const byNodeParent = {};
    let idx = 0;
    Object.values(flowNodes).forEach((n) => {
      if (n?.type === 'DATA_SET') {
        const nexsetId = n.data_set_id ?? n.id ?? null;
        if (nexsetId != null) {
          if (n.origin_node_id != null && byNodeOrigin[String(n.origin_node_id)] === undefined) {
            byNodeOrigin[String(n.origin_node_id)] = { nexsetId, idx };
          }
          if (n.parent_node_id != null && byNodeParent[String(n.parent_node_id)] === undefined) {
            byNodeParent[String(n.parent_node_id)] = { nexsetId, idx };
          }
        }
      }
      idx += 1;
    });

    return { byDatasetOrigin, byNodeOrigin, byNodeParent };
  },
);

/**
 * Resolve a nexset id for a BYO canvas node from the memoized {@link
 * getNexsetLookup} maps. `parentNodeIdCandidate` is the owning item's own
 * `flow_node_id` (NOT `flowId`) — matches the original inline selector's
 * `parent_node_id` fallback exactly. Returns null if nothing matches; the
 * final "check my own children" fallback stays with the caller since it only
 * scans one node's children, not the whole store.
 */
export function resolveNexsetId(lookup, flowId, parentNodeIdCandidate) {
  const byOrigin = flowId != null ? lookup.byDatasetOrigin[String(flowId)] : undefined;
  if (byOrigin != null) return byOrigin;

  const origin = flowId != null ? lookup.byNodeOrigin[String(flowId)] : undefined;
  const parent =
    parentNodeIdCandidate != null ? lookup.byNodeParent[String(parentNodeIdCandidate)] : undefined;
  if (origin && parent) return origin.idx <= parent.idx ? origin.nexsetId : parent.nexsetId;
  if (origin) return origin.nexsetId;
  if (parent) return parent.nexsetId;
  return null;
}

/**
 * Memoized flowId (origin_node_id) → first DATA_SINK flow node map, built once
 * per flowNodes change. Replaces the per-render
 * `Object.values(flowNodes).find(...)` scan LastRunNode's BYO Run Now gate ran
 * inline. First match in entity iteration order wins, same as `.find()`.
 */
export const getSinkNodeByFlowId = createSelector([getFlowNodes], (flowNodes) => {
  const bySinkOrigin = {};
  Object.values(flowNodes).forEach((n) => {
    if (n?.type !== 'DATA_SINK') return;
    const originId = String(n?.origin_node_id ?? '');
    if (bySinkOrigin[originId] === undefined) bySinkOrigin[originId] = n;
  });
  return bySinkOrigin;
});

const FallbackFlowDeletionStatus = {
  canDelete: false,
  hasActiveResources: false,
  reason: 'Flow not found',
};

/**
 * Computes whether a flow can be deleted based on the status of all its resources.
 * A flow can only be deleted if ALL resources (sources, datasets, sinks) are NOT in ACTIVE status.
 */
export const getFlowDeletionStatus = createSelector(
  [
    (flowNodeId) => flowNodeId,
    (_flowNodeId, ctx = {}) => ctx.entities || {},
    (_flowNodeId, ctx = {}) => ctx.branches || {},
    (_flowNodeId, ctx = {}) => ctx.flowNodes || {},
  ],
  (flowNodeId, entities, branches, flowNodes) => {
    if (!flowNodeId || !branches[flowNodeId]) return FallbackFlowDeletionStatus;

    const branchItems = branches[flowNodeId].items || [];

    const hasActiveResources = branchItems.some((nodeId) => {
      const flowNode = flowNodes[nodeId];
      if (!flowNode) return false;
      const resource = getResourceEntity(flowNode, entities, false);
      return resource?.status === 'ACTIVE';
    });

    return {
      hasActiveResources,
      canDelete: !hasActiveResources,
      reason: hasActiveResources ? 'Flow has active resources' : null,
    };
  },
);
